import { ChangeEvent, FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import clsx from 'clsx'
import debounce from 'debounce'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { flightSearchesStore, popularDirectionAviaStore } from 'widgets/avia'

import { getOrCreateAviaSearchId } from 'entities/ticket/lib/avia-search-id'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import listTicketsStore from 'entities/ticket/models/list-tickets/list-tickets-store'

import { SearchApi } from 'shared/api/SearchApi'
import { FlightSearchRecommendation } from 'shared/api/avia/flight-searches'
import ImageGlass from 'shared/images/glasss.png'
import { SearchCityBookit } from 'shared/models/general.ts'
import { AppIcon } from 'shared/ui/app-icon'
import { AllIcons } from 'shared/ui/app-icon/types'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyResults } from 'shared/ui/empty-result'
import { Header } from 'shared/ui/header'
import { Loader } from 'shared/ui/loader'
import { InfoSameDirection } from 'shared/ui/models'
import { SearchInput } from 'shared/ui/search-input'
import { ItemResult, SearchResultBlock } from 'shared/ui/search-result-block'

import { RecentSearchesSection } from './components/recent-searches-section'
import { mapRecommendationToFindTicketForm } from './lib/map-recommendation-to-form'
import s from './styles.module.scss'

interface Props {}

enum TYPE_SEARCH {
  SEARCH = 'search',
  START = 'start',
  LOADING = 'loading',
}

enum DIRECTION {
  FROM = 'from',
  TO = 'to',
}

const SearchDirection: FC<Props> = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { openDialog, closeDialog } = useDialogBottomSheet()

  const [params] = useSearchParams()

  const isSearch = !!params.get('page')
  const difficultRoute = params.get('difficult')
  const isDifficultSegmentSearch = difficultRoute !== null

  const { direction } = useParams()

  const [status, setStatus] = useState<TYPE_SEARCH>(TYPE_SEARCH.START)
  const [listSearch, setListSearch] = useState<SearchCityBookit[]>()

  if (direction !== DIRECTION.FROM && direction !== DIRECTION.TO) {
    return null
  }

  const popularDirectionsList =
    popularDirectionAviaStore.getPopularDirectionsList()
  const recentSearches = flightSearchesStore.getSearches()

  const onClick = () => {
    navigate(-1)
  }

  const getPopularDirections = async () => {
    if (popularDirectionsList) {
      return
    }
    popularDirectionAviaStore.fetchPopularDirectionsList()
  }

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const searchingValue = event?.target?.value

    if (searchingValue?.length) {
      setStatus(TYPE_SEARCH.LOADING)

      try {
        const { data: CountrySearch } =
          await SearchApi.searchCityBookit(searchingValue)
        setStatus(TYPE_SEARCH.SEARCH)

        setListSearch(CountrySearch?.data)
      } catch (e) {
        new Error('[Error on search city]')
      }
    } else {
      setStatus(TYPE_SEARCH.START)
    }
  }

  const onResetValue = () => {
    setStatus(TYPE_SEARCH.START)
  }

  const onRepeatSearch = (item: FlightSearchRecommendation) => {
    const form = mapRecommendationToFindTicketForm(item)

    findTicketFormStore.setFormFindTicket(form)
    findTicketFormStore.resetFilter()
    getOrCreateAviaSearchId(findTicketFormStore.formFindTicket)
    listTicketsStore.resetListOfTicket()
    navigate(RoutingPaths.avia.search.path())
  }

  const onSelectDirection = (data: ItemResult) => {
    const numberDirection = parseInt(difficultRoute || '0')

    const currentForm = isSearch
      ? findTicketFormStore?.copyFormFindTicket &&
        findTicketFormStore?.copyFormFindTicket.directions?.[numberDirection]
      : findTicketFormStore?.formFindTicket.directions?.[numberDirection]

    const directionData = {
      cityName: data.title!,
      countryName: data.subTitle!,
      cityIataCode: data.code!,
    }

    if (
      direction === DIRECTION.FROM &&
      currentForm?.to?.cityIataCode !== data.code
    ) {
      findTicketFormStore.onSelectDirectionFrom({
        depp: directionData,
        index: numberDirection,
        isCopy: isSearch,
      })
      navigate(-1)
    } else if (
      direction === DIRECTION.TO &&
      currentForm?.from?.cityIataCode !== data.code
    ) {
      findTicketFormStore.onSelectDirectionTo({
        depp: directionData,
        index: numberDirection,
        isCopy: isSearch,
      })

      navigate(-1)
    } else {
      openDialog({
        isDialog: false,
        child: <InfoSameDirection onCloseDirection={closeDialog} />,
      })
    }
  }

  const titlePage = direction === DIRECTION.FROM ? t('from') : t('to')

  useEffect(() => {
    getPopularDirections()
  }, [])

  return (
    <div className={s.container}>
      <div className={s.header}>
        <Header
          title={titlePage}
          actionLeft={
            <AppIcon
              onClick={onClick}
              className={s.icon}
              id={AllIcons.ARROW_LEFT}
            />
          }
        />
      </div>

      <div className={clsx(s.search, 'container')}>
        <SearchInput
          size="large"
          placeholder={t('search_by_city_and_airport')}
          onReset={onResetValue}
          onChange={debounce(onChange, 1000)}
        />
      </div>

      {!isDifficultSegmentSearch &&
        status !== TYPE_SEARCH.SEARCH &&
        status !== TYPE_SEARCH.LOADING && (
          <RecentSearchesSection
            items={recentSearches}
            onRepeatSearch={onRepeatSearch}
            isLoading={flightSearchesStore.isLoading}
          />
        )}

      {status === TYPE_SEARCH.LOADING ? <Loader /> : null}

      {status === TYPE_SEARCH.START && (
        <div>
          <div className={clsx(s.title, 'container')}>
            <p>{t('popular_direction')}</p>
          </div>

          {popularDirectionsList?.map((item, index) => (
            <div
              className={clsx(s.item, 'container')}
              key={index}
            >
              <SearchResultBlock
                onSelect={() =>
                  onSelectDirection({
                    image: item?.logo,
                    title: item?.city.title,
                    subTitle: item?.country,
                    code: item?.airport.code,
                  })
                }
                {...{
                  image: item?.logo,
                  title: item?.country,
                  subTitle: item?.city.title,
                  code: item?.airport.code,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {status === TYPE_SEARCH.SEARCH &&
        listSearch?.map((result, index) => (
          <div
            className={clsx(s.item, 'container')}
            key={index}
          >
            <SearchResultBlock
              onSelect={(data) => onSelectDirection(data)}
              {...{
                title: result?.country,
                subTitle: result?.name,
                code: result?.code,
                // code: result?.cityIataCode,
                // child: result?.airports?.map((airport) => ({
                //   title: airport?.airportName,
                //   code: airport?.airportIataCode,
                // })),
              }}
            />
          </div>
        ))}

      {status === TYPE_SEARCH.SEARCH && listSearch?.length === 0 && (
        <div className={clsx('container', s.emptyBlock)}>
          <EmptyResults
            image={
              <img
                src={ImageGlass}
                alt="test"
                className={s.image}
              />
            }
            title={t('not_fount_title')}
            subTitle={t('not_fount_sub_title')}
          />
        </div>
      )}
    </div>
  )
})

export default SearchDirection
